'use client'

import {
  useGetUserFeaturedImagesQuery,
  useSelectFeaturedImagesMutation,
  GetUserFeaturedImagesQueryRootKey,
} from '@web/gen/service/creativefabrica/social/v2'
import { usePublicUserProfileQuery, useUserQuery } from 'codegen/generated/user'
import Image from 'next/image'
import * as React from 'react'
import { motion } from 'framer-motion'
import { useFeaturedImagesDialog } from './contexts/featured-images-dialog-context'
import {
  IconChevronLeft,
  IconChevronRight,
  IconDelete,
  IconEdit,
  IconPhotoPrints,
} from '@repo/design-system-cf/components/icons'
import { SkeletonItem } from 'components/skeleton-item'
import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import type { GetUserFeaturedImagesResponse } from '@web/gen/service/creativefabrica/social/v2/service_pb'
import { useParams } from 'next/navigation'
import * as Popover from 'primitives/popover'
import { useConfirmationModal } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/_components/confirmation-modal/confirmation-modal-provider'
import { useQueryClient } from '@tanstack/react-query'
// oxlint-disable-next-line complexity
export const CrafterImagesHeaderClient = ({
  images,
}: {
  images: GetUserFeaturedImagesResponse['images']
}) => {
  const { openDialog } = useFeaturedImagesDialog()
  const { data: user } = useUserQuery(undefined, {
    select: data => data.me?.user,
  })
  const { slug } = useParams()
  const queryClient = useQueryClient()

  const { data: publicProfile } = usePublicUserProfileQuery(
    { slug: slug as string },
    { select: d => d?.publicUserProfile, enabled: Boolean(slug) },
  )

  const { data: userFeaturedImagesClient, isFetchedAfterMount } = useGetUserFeaturedImagesQuery(
    {
      userSlug: slug as string,
    },
    {
      enabled: Boolean(slug),
      initialData:
        images.length > 0
          ? {
              images,
              $typeName: 'creativefabrica.social.v2.GetUserFeaturedImagesResponse' as const,
            }
          : undefined,
    },
  )

  const isSelf =
    Boolean(user?.id) &&
    Boolean(publicProfile?.id) &&
    (user?.id ?? '') === (publicProfile?.id ?? '')

  // Use fallback for initial SSR, but after query fetches fresh data, use query data exclusively
  const userFeaturedImages = isFetchedAfterMount
    ? (userFeaturedImagesClient?.images ?? [])
    : userFeaturedImagesClient?.images || images
  // Get the actual number of available images

  const selectImageIndex = (imageId: string | undefined, index: number) => {
    if (index <= 0 || index > 3) {
      return
    }
    openDialog(imageId, index, user?.id)
  }

  const selectImageMutation = useSelectFeaturedImagesMutation()

  const handleDeleteImage = (imageId: string) => {
    const imagesToDelete = userFeaturedImages?.filter(img => img?.id !== imageId)
    selectImageMutation.mutate(
      {
        images: imagesToDelete?.map(img => ({
          imageId: img?.id,
          order: img?.position,
        })),
      },
      {
        onSuccess: () => {
          // Invalidate all getUserFeaturedImages queries to trigger refetch in all components
          void queryClient.invalidateQueries({
            queryKey: [GetUserFeaturedImagesQueryRootKey],
          })
        },
      },
    )
  }

  const isLoading = false

  return (
    <>
      {/* DESKTOP */}
      <div
        className={tw(
          'w-full px-2 hidden tablet:block min-h-fit ',
          (userFeaturedImages && userFeaturedImages?.length > 0) || isSelf ? ' pb-5' : null,
        )}
      >
        <div
          className={tw(
            'grid grid-cols-3 w-full overflow-hidden select-none gap-5',
            // (userFeaturedImages && userFeaturedImages?.length > 0) || isSelf ? ' pt-3' : null,
          )}
        >
          {isSelf === true ? (
            <>
              <div className="col-span-1 aspect-[3/2]">
                <div className="relative w-full h-full rounded-[24px] translate-x-[20%] translate-y-4 rotate-6 shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={0}
                    isSelf={isSelf}
                  />
                </div>
              </div>
              <div className="col-span-1 aspect-[3/2] ">
                <div className="relative z-[2] w-full h-full rounded-[24px] translate-y-3 -rotate-[4deg] shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={1}
                    isSelf={isSelf}
                  />
                </div>
              </div>
              <div className="col-span-1 aspect-[3/2] ">
                <div className="relative w-full h-full rounded-[24px] -translate-x-[20%] translate-y-5 rotate-6 object-cover shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={2}
                    isSelf={isSelf}
                  />
                </div>
              </div>
            </>
          ) : null}
          {/* 3 images  case */}
          {userFeaturedImages?.length === 3 && !isSelf ? (
            <>
              <div className="col-span-1 aspect-[3/2]">
                <div className="relative w-full h-full rounded-[24px] translate-x-[20%] translate-y-4 rotate-6 shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={0}
                    isSelf={isSelf}
                  />
                </div>
              </div>
              <div className="col-span-1 aspect-[3/2] ">
                <div className="relative z-[2] w-full h-full rounded-[24px] translate-y-3 -rotate-[4deg] shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={1}
                    isSelf={isSelf}
                  />
                </div>
              </div>
              <div className="col-span-1 aspect-[3/2] ">
                <div className="relative w-full h-full rounded-[24px] -translate-x-[20%] translate-y-5 rotate-6 object-cover shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={2}
                    isSelf={isSelf}
                  />
                </div>
              </div>
            </>
          ) : null}
          {/* 2 images  case */}
          {userFeaturedImages?.length === 2 && !isSelf ? (
            <div className="flex col-span-3 flex-row justify-center gap-4 max-h-[200px]">
              <div className=" aspect-[3/2]">
                <div className="relative w-full h-full translate-x-[30px] translate-y-4 rounded-[24px] rotate-6 shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={(userFeaturedImages[0]?.position ?? 1) - 1}
                    isSelf={isSelf}
                  />
                </div>
              </div>
              <div className="aspect-[3/2] ">
                <div className="relative z-[2] w-full h-full -translate-x-[30px]  rounded-[24px] translate-y-3 -rotate-[4deg] shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={(userFeaturedImages[1]?.position ?? 2) - 1}
                    isSelf={isSelf}
                  />
                </div>
              </div>
            </div>
          ) : null}
          {/* 1 image  case */}
          {userFeaturedImages?.length === 1 && !isSelf ? (
            <div className="col-span-3 grid place-items-center min-h-[200px] max-h-[240px]">
              <div className="w-full max-w-sm aspect-[3/2]">
                <div className="relative w-full h-full translate-y-4 rounded-[24px] -rotate-[4deg] shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group">
                  <HeaderImageCard
                    isLoading={isLoading}
                    userFeaturedImages={userFeaturedImages}
                    selectImageIndex={selectImageIndex}
                    handleDeleteImage={handleDeleteImage}
                    index={(userFeaturedImages[0]?.position ?? 1) - 1}
                    isSelf={isSelf}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {(userFeaturedImages?.length && userFeaturedImages?.length > 0) || isSelf ? (
          <div className="w-full h-[2px] -mt-px bg-gradient-radial from-cf-transparent-grey-1000-20 to-cf-transparent-grey-1000-0 to-[80%]" />
        ) : null}
      </div>
      {/* MOBILE */}
      <CrafterImagesHeaderMobile
        userFeaturedImages={userFeaturedImages}
        isSelf={isSelf}
        selectImageIndex={selectImageIndex}
        handleDeleteImage={handleDeleteImage}
      />
    </>
  )
}

export const CrafterImagesHeaderMobile = ({
  userFeaturedImages,
  isSelf,
  selectImageIndex,
  handleDeleteImage,
}: {
  userFeaturedImages: GetUserFeaturedImagesResponse['images']
  isSelf: boolean
  selectImageIndex(imageId: string | undefined, index: number): void
  handleDeleteImage(imageId: string): void
}) => {
  const [currentCardIndex, setCurrentCardIndex] = React.useState(0)
  const [previousCardIndex, setPreviousCardIndex] = React.useState(0)

  const [isAnimating, setIsAnimating] = React.useState(false)

  const isLoading = false

  // When isSelf is true, show all 3 positions in order (even if missing)
  // When isSelf is false, only show images that exist
  const orderedUserFeaturedImages = isSelf
    ? // For self: create array with all 3 positions, filling in existing images
      [1, 2, 3].map(position => {
        return (
          userFeaturedImages?.find(img => img?.position === position) || {
            position,
            id: undefined,
          }
        )
      })
    : // For others: filter and sort existing images
      userFeaturedImages
        ?.filter(img => img?.id)
        ?.toSorted((a, b) => (a?.position ?? 0) - (b?.position ?? 0))

  // imageCount: when isSelf, count all positions (3), otherwise count actual images
  const imageCount = orderedUserFeaturedImages?.length ?? 0

  const handlePrevious = () => {
    if (isAnimating || imageCount <= 1) {
      return
    }
    setIsAnimating(true)
    setPreviousCardIndex(currentCardIndex)
    setCurrentCardIndex(prev => {
      // For 2 images: toggle between 0 and 1 (no cycling)
      if (imageCount === 2) {
        return prev === 0 ? 1 : 0
      }
      // For 3 images: cycle through 0, 1, 2
      return prev === 0 ? imageCount - 1 : prev - 1
    })
    setTimeout(() => {
      setIsAnimating(false)
    }, 400)
  }

  const handleNext = () => {
    if (isAnimating || imageCount <= 1) {
      return
    }
    setIsAnimating(true)
    setPreviousCardIndex(currentCardIndex)
    setCurrentCardIndex(prev => {
      // For 2 images: toggle between 0 and 1 (no cycling)
      if (imageCount === 2) {
        return prev === 1 ? 0 : 1
      }
      // For 3 images: cycle through 0, 1, 2
      return prev === imageCount - 1 ? 0 : prev + 1
    })
    setTimeout(() => {
      setIsAnimating(false)
    }, 400)
  }

  const getCardRotation = (cardIndex: number) => {
    const isCurrentCard = currentCardIndex === cardIndex

    // When current, rotate to 0 degrees
    if (isCurrentCard) {
      return 0
    }

    // Otherwise use base rotation
    const rotationDegrees = [3, -5, -3]
    return rotationDegrees[cardIndex] || rotationDegrees[0]
  }

  const getCardOpacityValue = (cardIndex: number) => {
    const isCurrentCard = currentCardIndex === cardIndex
    const isPreviousCard = cardIndex === previousCardIndex

    // During animation, fade out previous and fade in current
    if (isAnimating) {
      if (isCurrentCard) {
        // New card fades in (from semi-transparent to fully opaque)
        return 1
      } else if (isPreviousCard) {
        // Old card fades out (from fully opaque to semi-transparent)
        return 0.8
      }
      // Other cards stay semi-transparent (visible behind)
      return 0.8
    }

    // Not animating: current card is fully opaque, others are semi-transparent
    if (isCurrentCard) {
      return 1
    }

    // All other cards are semi-transparent (visible behind)
    return 1
  }

  // Reset current index if it's out of bounds when image count changes
  React.useEffect(() => {
    if (imageCount > 0 && currentCardIndex >= imageCount) {
      setCurrentCardIndex(0)
    }
  }, [imageCount, currentCardIndex])

  // For non-self, don't show if no images
  if (!isSelf && (!orderedUserFeaturedImages || orderedUserFeaturedImages.length === 0)) {
    return null
  }

  return (
    <>
      <div className="w-full px-2 mb-10 block tablet:hidden overflow-hidden">
        <div className="w-full aspect-[3/2]  relative grid place-items-center p-5 select-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2  w-full z-10 -translate-y-1/2 pointer-events-none">
            {orderedUserFeaturedImages &&
            orderedUserFeaturedImages?.length &&
            orderedUserFeaturedImages?.length > 1 ? (
              <div className="flex justify-between items-center pt-4 px-2">
                <Button
                  variant="tertiary"
                  size="small"
                  className="shadow-[0_4px_8px_3px_cf-grey-trasnparent-1000-16,_0_1px_2px_0_cf-grey-trasnparent-1000-12] pointer-events-auto"
                  iconLeft={<IconChevronLeft className="size-4" />}
                  onClick={handlePrevious}
                />
                <Button
                  variant="tertiary"
                  size="small"
                  className="shadow-[0_4px_8px_3px_cf-grey-trasnparent-1000-16,_0_1px_2px_0_cf-grey-trasnparent-1000-12] pointer-events-auto"
                  iconLeft={<IconChevronRight className="size-4" />}
                  onClick={handleNext}
                />
              </div>
            ) : null}
          </div>
          {/* Render images in order by position */}
          {orderedUserFeaturedImages?.map((image, arrayIndex: number) => {
            // Use position - 1 as the index for HeaderImageCard (position 1 -> index 0, position 2 -> index 1, etc.)
            const positionIndex = (image?.position ?? arrayIndex + 1) - 1

            // Determine z-index recursively: when swiping right, current goes to bottom, next comes to top
            const getZIndex = () => {
              if (currentCardIndex === arrayIndex) {
                return 'z-[3]'
              }

              // Calculate relative position from current index (circular)
              let relativePosition = arrayIndex - currentCardIndex
              if (relativePosition < 0) {
                relativePosition += imageCount
              }

              // For 2 images: the other image is both next and previous
              if (imageCount === 2) {
                // The other image comes forward
                return 'z-[2]'
              }

              // For 3+ images: next comes forward, previous goes to bottom
              if (relativePosition === 1) {
                // Next image comes to top (second highest)
                return 'z-[2]'
              } else if (relativePosition === imageCount - 1) {
                // Previous image goes to bottom (lowest)
                return 'z-0'
              }
              // Other images stay in middle
              return 'z-[1]'
            }

            const animateOpacity = getCardOpacityValue(arrayIndex)
            const animateRotate = getCardRotation(arrayIndex)

            return (
              <motion.div
                key={image?.id || `position-${image?.position ?? arrayIndex + 1}`}
                className={tw(
                  'absolute top-5 left-5 right-5 aspect-[3/2] translate-y-10 rounded-[24px] shadow-[0_4px_8px_3px_#13131529,_0_1px_2px_0_#1313151F] bg-cf-grey-100 group',
                  getZIndex(),
                )}
                animate={{
                  opacity: animateOpacity,
                  rotate: animateRotate,
                }}
                transition={{
                  opacity: {
                    duration: 0.4,
                    ease: 'easeInOut',
                  },
                  rotate: {
                    duration: 0.4,
                    ease: 'easeInOut',
                  },
                }}
                initial={false}
              >
                <HeaderImageCard
                  isLoading={isLoading}
                  userFeaturedImages={userFeaturedImages}
                  selectImageIndex={selectImageIndex}
                  handleDeleteImage={handleDeleteImage}
                  index={positionIndex}
                  isSelf={isSelf}
                />
              </motion.div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export const HeaderImageCard = ({
  isLoading,
  userFeaturedImages,
  selectImageIndex,
  handleDeleteImage,
  index,
  isSelf,
}: {
  isLoading: boolean
  userFeaturedImages: GetUserFeaturedImagesResponse['images']
  selectImageIndex(imageId: string | undefined, index: number): void
  handleDeleteImage(imageId: string): void
  index: number
  isSelf: boolean
}) => {
  const image = userFeaturedImages?.find(img => index + 1 === img.position)
  const { openModal } = useConfirmationModal()

  const handleDeleteImageClick = (imageId: string) => {
    openModal({
      title: 'Delete featured project?',
      message: 'Are you sure you want to delete this featured project?',
      confirmText: 'Delete',
      cancelText: 'No',
      onConfirm: () => handleDeleteImage(imageId),
    })
  }

  return (
    <>
      {isLoading ? (
        <div className="w-full h-full">
          <SkeletonItem className="w-full h-full rounded-[24px] bg-cf-grey-100" />
        </div>
      ) : image?.id ? (
        <>
          <Image
            src={(image?.optimizedImageUrl || image?.imageUrl) ?? ''}
            alt="User Featured Image"
            width={300}
            height={200}
            className="w-full h-full object-cover rounded-[24px] "
            // onClick={() => (isSelf ? selectImageIndex(image?.id, index + 1) : null)}
          />
          {isSelf ? (
            <>
              <Popover.Root>
                <Popover.Trigger asChild>
                  <div
                    className={tw(
                      'absolute flex justify-center cursor-pointer items-center tablet:opacity-0 tablet:group-hover:opacity-100 transition-opacity duration-200',
                      index === 2 ? 'top-4 tablet:right-4 tablet:left-auto left-4' : 'top-4 left-4',
                    )}
                  >
                    <Button variant="primary-inverse" size="small" iconLeft={<IconEdit />} />
                  </div>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content className={tw('z-50')}>
                    <div className="w-full h-full flex p-1 flex-col bg-cf-white rounded-2  text-cf-grey-800 text-14 font-medium ">
                      <div
                        className="w-full flex justify-start items-center gap-1 p-2 hover:bg-cf-grey-100 cursor-pointer active:bg-cf-white rounded-2"
                        onClick={() => selectImageIndex(undefined, index + 1)}
                      >
                        <IconPhotoPrints className="size-5 text-cf-grey-800 " />
                        Change project
                      </div>
                      <div
                        className="w-full flex justify-start items-center gap-1 p-2 hover:bg-cf-grey-100 cursor-pointer active:bg-cf-white rounded-2"
                        onClick={() => handleDeleteImageClick(image?.id ?? '')}
                      >
                        <IconDelete className="size-5 text-cf-grey-800 " />
                        Delete
                      </div>
                    </div>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </>
          ) : null}
        </>
      ) : (
        <div className="w-full flex h-full justify-center items-center rounded-[24px] bg-cf-grey-100 shadow-[0_4px_8px_3px_cf-grey-trasnparent-1000-16,_0_1px_2px_0_cf-grey-trasnparent-1000-12] border border-dashed border-cf-transparent-grey-1000-12 ">
          {isSelf ? (
            <Button
              variant="tertiary"
              size="small"
              iconLeft={<IconPhotoPrints className="size-4" />}
              onClick={() => selectImageIndex(undefined, index + 1)}
            >
              Add featured project
            </Button>
          ) : null}
        </div>
      )}
    </>
  )
}
